import * as yup from 'yup';
import { RendererType } from './renderer-type';
import { KpiConfigPeriod } from '../company-financials.data-model';

/**
 * Mirrors the BE FieldDefinition table. Intentionally thin —
 * presentation (renderer/formatter/formMeta/gridMeta) is derived
 * from `type` + `format` via the registry, not stored inline.
 *
 * Defined as a yup schema so each phase's `xxxToFieldDefinition` mapper
 * can terminate in `fieldDefinitionSchema.cast(...)` for consistent
 * defaults, coercion, and validation. Hand-written factories and type
 * guards are thin wrappers over the schema.
 */
export const FieldPrimitiveType = {
  number: 'number',
  string: 'string',
  boolean: 'boolean',
  date: 'date',
  array: 'array',
} as const;
export type FieldPrimitiveType = (typeof FieldPrimitiveType)[keyof typeof FieldPrimitiveType];

/**
 * KPI scenario, consumed only on KPI-scoped fields. Mirrors the legacy
 * `KpiConfigScenario` enum in `field2.data-model.ts` so the schema doesn't
 * import the legacy file. Once Phase 5 deletes `IField`, the legacy enum
 * goes with it and this becomes the single definition.
 */
export const KpiFieldScenario = {
  accrued: 'accrued',
  endOfPeriod: 'endOfPeriod',
  none: 'none',
} as const;
export type KpiFieldScenario = (typeof KpiFieldScenario)[keyof typeof KpiFieldScenario];

/**
 * KPI section selection (Actual / Budget / Both), consumed only on KPI-scoped
 * fields. Mirrors the legacy `KpiConfigSection` enum.
 */
export const KpiFieldSectionType = {
  actual: 'actual',
  budget: 'budget',
  both: 'both',
} as const;
export type KpiFieldSectionType = (typeof KpiFieldSectionType)[keyof typeof KpiFieldSectionType];

/**
 * Distinguishes system / universal / user KPIs on KPI-scoped fields.
 * Replaces direct reads of `IField.entity` (e.g. `isUniversalKPIField`)
 * for consumers migrated to `FieldDefinition`. The legacy `IField.entity`
 * carries finer-grained `'systemKPI'` / `'universalKPI'` / `'userKPI'`
 * values that all collapse to `scope: 'kpi'` on the schema; this enum
 * preserves the distinction for display logic that needs it.
 */
export const KpiEntityType = {
  system: 'system',
  universal: 'universal',
  user: 'user',
} as const;
export type KpiEntityType = (typeof KpiEntityType)[keyof typeof KpiEntityType];

/**
 * Element type for an array-typed `FieldDefinition`, or a typed reference.
 * Hand-written discriminated union — yup v1's lazy/oneOf typings don't
 * infer this cleanly. The schema validates the same shape at runtime
 * via `itemTypeRefSchema`.
 */
export type ItemTypeRef =
  | { kind: 'primitive'; type: FieldPrimitiveType }
  | { kind: 'reference'; key: string };

const enumOptionSchema = yup.object({
  value: yup.string().required(),
  label: yup.string().required(),
  group: yup.string().optional(),
});

export type EnumOption = yup.InferType<typeof enumOptionSchema>;

const itemTypeRefSchema = yup
  .object({
    kind: yup
      .string()
      .oneOf(['primitive', 'reference'] as const)
      .required(),
    type: yup.string().oneOf(Object.values(FieldPrimitiveType)).optional(),
    key: yup.string().optional(),
  })
  .test('itemTypeRef-shape', 'itemType requires `type` for primitive or `key` for reference', (v) => {
    if (!v) {
      return true;
    }
    if (v.kind === 'primitive') {
      return typeof v.type === 'string';
    }
    if (v.kind === 'reference') {
      return typeof v.key === 'string';
    }
    return false;
  });

/**
 * The `format` values allowed per primitive type. Extend deliberately —
 * the schema and the presentation registry both gate on this list.
 *
 * Entries marked "legacy — accepted for existing data only" are deprecated
 * `RendererType` values retained so existing records keep validating during
 * the migration. New `FieldDefinition` records should not use them; prefer
 * the non-deprecated renderer with formatter configuration instead.
 */
export const ALLOWED_FORMATS_BY_TYPE: Record<FieldPrimitiveType, ReadonlyArray<RendererType>> = {
  number: [
    RendererType.number,
    RendererType.currencyId,
    // legacy — accepted for existing data only
    RendererType.integer,
    RendererType.percent,
    RendererType.multiplier,
    RendererType.currency,
    RendererType.numeric,
  ],
  string: [
    RendererType.text,
    RendererType.singleSelect,
    RendererType.multiSelect,
    RendererType.id,
    RendererType.colorStatus,
    RendererType.colorStatusWithLabel,
    RendererType.companyId,
    RendererType.companySearch,
    RendererType.companySocialMedia,
    RendererType.companyWebsite,
    RendererType.location,
    // legacy — accepted for existing data only
    RendererType.fundType,
  ],
  boolean: [RendererType.boolean],
  date: [RendererType.date],
  array: [
    RendererType.multiSelect,
    RendererType.freeLabels,
    RendererType.fileUpload,
    RendererType.fileUpload2,
    RendererType.kpiTable,
    RendererType.universalKpiTable,
  ],
};

export function isAllowedFormat(
  type: FieldPrimitiveType,
  format: string | undefined
): format is RendererType | undefined {
  if (format == null) {
    return true;
  }
  return (ALLOWED_FORMATS_BY_TYPE[type] as ReadonlyArray<string>).includes(format);
}

/**
 * `.oneOf()` is a validate-time rule, not cast-time. Without this transform,
 * an enum value the BE adds (e.g. a new `kpiScenario` variant) would silently
 * pass `cast()` and leak onto `FieldDefinition` as an unknown string. The
 * transform drops unknowns to `undefined`, so optional fields stay optional
 * and fields with `.default()` fall back to their default.
 */
function dropUnknownEnumValue<T extends string>(allowed: readonly T[]) {
  return (value: unknown) => {
    if (typeof value !== 'string') {
      return value;
    }
    return (allowed as readonly string[]).includes(value) ? value : undefined;
  };
}

export const fieldDefinitionSchema = yup.object({
  // String fields use `.defined()` not `.required()` because yup v1's `.required()`
  // rejects empty strings, and `''` is a valid initial value during creation
  // (matches the legacy `createFieldDefinition` behavior).
  key: yup.string().defined().default(''),
  displayName: yup.string().defined().default(''),
  description: yup.string().optional(),
  type: yup
    .string<FieldPrimitiveType>()
    .transform(dropUnknownEnumValue(Object.values(FieldPrimitiveType)))
    .oneOf(Object.values(FieldPrimitiveType))
    .defined()
    .default(FieldPrimitiveType.string),
  /**
   * Drives FE renderer/formatter selection via the presentation registry.
   * Typed as `string` to mirror the BE schema (VARCHAR(50)). The schema's
   * test rejects `(type, format)` combos absent from `ALLOWED_FORMATS_BY_TYPE`;
   * `isAllowedFormat` is the matching type predicate for callers that want
   * to narrow `string` to `RendererType`.
   */
  format: yup
    .string()
    .optional()
    .test('allowed-format-for-type', 'Format is not allowed for the given type', function (value) {
      if (value === undefined) {
        return true;
      }
      const parent = this.parent as { type?: unknown };
      if (typeof parent.type !== 'string') {
        return true;
      }
      return isAllowedFormat(parent.type as FieldPrimitiveType, value);
    }),
  sortOrder: yup.number().defined().default(0),
  enumOptions: yup.array(enumOptionSchema).optional(),
  itemType: itemTypeRefSchema.optional().default(undefined),
  isActive: yup.boolean().defined().default(true),
  createdAt: yup.string().defined().default(''),
  updatedAt: yup.string().defined().default(''),
  // KPI-scoped optional fields. Populated by `kpiConfigToFieldDefinition`;
  // left undefined for non-KPI scopes. The schema does not enforce
  // `scope === 'kpi'` because the BE does not require these either —
  // existing KPI rows can have any subset missing.
  kpiGroupId: yup.number().optional(),
  kpiScenario: yup
    .string<KpiFieldScenario>()
    .transform(dropUnknownEnumValue(Object.values(KpiFieldScenario)))
    .oneOf(Object.values(KpiFieldScenario))
    .optional(),
  periods: yup
    .array(yup.string<KpiConfigPeriod>().oneOf(Object.values(KpiConfigPeriod)).required())
    .optional()
    .transform((value: unknown) => {
      if (!Array.isArray(value)) {
        return value;
      }
      const allowed = Object.values(KpiConfigPeriod) as string[];
      return value.filter((v) => typeof v === 'string' && allowed.includes(v));
    }),
  sectionType: yup
    .string<KpiFieldSectionType>()
    .transform(dropUnknownEnumValue(Object.values(KpiFieldSectionType)))
    .oneOf(Object.values(KpiFieldSectionType))
    .optional(),
  yearCalcType: yup.string().optional(),
  kpiEntityType: yup
    .string<KpiEntityType>()
    .transform(dropUnknownEnumValue(Object.values(KpiEntityType)))
    .oneOf(Object.values(KpiEntityType))
    .optional(),
});

type InferredFieldDefinition = yup.InferType<typeof fieldDefinitionSchema>;

/**
 * `itemType` is overridden with the precise discriminated union; the schema
 * validates the same shape at runtime, but yup v1's inference flattens it.
 */
export type FieldDefinition = Omit<InferredFieldDefinition, 'itemType'> & {
  itemType?: ItemTypeRef;
};

export function createFieldDefinition(overrides: Partial<FieldDefinition> = {}): FieldDefinition {
  return fieldDefinitionSchema.cast(overrides, { stripUnknown: true }) as FieldDefinition;
}

export function isFieldDefinition(value: unknown): value is FieldDefinition {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  return fieldDefinitionSchema.isValidSync(value, { strict: true });
}
